import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef as MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import {
  IStoreConfigForm,
  IStoreParameters
} from './../../../../models/storeConfig';
import { IStoreConfig } from 'src/app/models/storeConfig';
import { SnackbarService } from './../../../../services/snackbar.service';
import { ApiStoreService } from './../../../../services/api/api-store.service';
import { allowSaveWithPaymentValidator } from 'src/app/shared/validators/allowSaveWithPayment.validator';
import { allowSaveWithoutPaymentValidator } from 'src/app/shared/validators/allowSaveWithoutPayment.validator';

@Component({
  selector: 'app-modal-loja',
  templateUrl: './modal-loja.component.html',
  styleUrls: ['./modal-loja.component.scss']
})
export class ModalLojaComponent implements OnInit {
  storeConfig!: IStoreConfig;
  parameters!: IStoreParameters;

  form!: FormGroup<IStoreConfigForm>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { storeConfig: IStoreConfig; parameters: IStoreParameters },
    private _dialogRef: MatDialogRef<ModalLojaComponent>,
    private _storeConfigService: ApiStoreService,
    private _snackbarService: SnackbarService
  ) {
    this.storeConfig = data.storeConfig;
    this.parameters = data.parameters;
  }

  ngOnInit(): void {
    this.buildForm();
    this.listenBeneficiaryTypeChanges();
  }

  buildForm(): void {
    this.form = new FormGroup<IStoreConfigForm>({
      Subsidiary: new FormControl<string>(
        { value: '', disabled: !!this.storeConfig?.StoreConfigId },
        { nonNullable: true, validators: [Validators.required] }
      ),
      Store: new FormControl<string>(
        { value: '', disabled: !!this.storeConfig?.StoreConfigId },
        { nonNullable: true, validators: [Validators.required] }
      ),
      Bank: new FormControl<string>(
        { value: '', disabled: !!this.storeConfig?.StoreConfigId },
        { nonNullable: true, validators: [Validators.required] }
      ),
      BeneficiaryType: new FormControl<string>(
        { value: '', disabled: !!this.storeConfig?.StoreConfigId },
        { nonNullable: true, validators: [Validators.required] }
      ),
      Product: new FormControl<string>(
        { value: '', disabled: !!this.storeConfig?.StoreConfigId },
        { nonNullable: true, validators: [Validators.required] }
      ),
      Commission: new FormControl<number | null>(
        { value: null, disabled: !!this.storeConfig?.BeneficiaryName },
        {
          nonNullable: true,
          validators: [
            allowSaveWithoutPaymentValidator(
              () => this.form?.controls['IsBeneficiaryDefined'].value
            )
          ]
        }
      ),
      CostCenter: new FormControl<string>(
        { value: '', disabled: !!this.storeConfig?.BeneficiaryName },
        {
          nonNullable: true,
          validators: [
            allowSaveWithoutPaymentValidator(
              () => this.form?.controls['IsBeneficiaryDefined'].value
            )
          ]
        }
      ),
      StartDate: new FormControl<string>(
        { value: '', disabled: !!this.storeConfig?.StoreConfigId },
        { nonNullable: true, validators: [Validators.required] }
      ),
      FinishDate: new FormControl<string>(
        { value: '', disabled: !!this.storeConfig?.StoreConfigId },
        { nonNullable: true, validators: [Validators.required] }
      ),
      SaleType: new FormControl<string>(
        { value: '', disabled: !!this.storeConfig?.StoreConfigId },
        { nonNullable: true, validators: [Validators.required] }
      ),
      BeneficiaryName: new FormControl<string>(
        { value: '', disabled: !!this.storeConfig?.StoreConfigId },
        {
          nonNullable: true,
          validators: [
            allowSaveWithPaymentValidator(
              () => this.form?.controls['IsBeneficiaryDefined'].value
            )
          ]
        }
      ),
      BeneficiaryDocument: new FormControl<string>(
        { value: '', disabled: !!this.storeConfig?.StoreConfigId },
        {
          nonNullable: true,
          validators: [
            allowSaveWithPaymentValidator(
              () => this.form?.controls['IsBeneficiaryDefined'].value
            )
          ]
        }
      ),
      IsBeneficiaryDefined: new FormControl<boolean>(false, {
        nonNullable: true
      }),
      Obs: new FormControl<string>('', { nonNullable: true })
    });

    if (this.storeConfig) {
      this.form.patchValue(this.storeConfig);
    }
  }

  listenBeneficiaryTypeChanges(): void {
    this.form.controls['BeneficiaryType'].valueChanges.subscribe({
      next: (response) => {
        if (response.toLowerCase() === 'pagadoria') {
          this.setCommissionDisabled();
        } else {
          this.setCommissionEnabled();
        }
      }
    });
  }

  setCommissionDisabled(): void {
    this.form.controls['Commission'].disable();
    this.form.controls['CostCenter'].disable();

    this.form.controls['Commission'].setValue(null);
    this.form.controls['CostCenter'].setValue('');

    this.form.controls['BeneficiaryName']?.setValue('');
    this.form.controls['BeneficiaryDocument']?.setValue('');

    this.form.controls['IsBeneficiaryDefined'].setValue(true);
  }

  setCommissionEnabled(): void {
    this.form.controls['Commission'].enable();
    this.form.controls['CostCenter'].enable();

    this.form.controls['Commission'].setValue(null);
    this.form.controls['CostCenter'].setValue('');

    this.form.controls['IsBeneficiaryDefined'].setValue(false);
  }

  saveStoreConfig(): void {
    const storeConfig: IStoreConfig = this.form.getRawValue();
    let message: string = this.storeConfig?.StoreConfigId
      ? 'Configuração atualizada com sucesso!'
      : 'Configuração criada com sucesso!';

    if (!!this.storeConfig?.StoreConfigId)
      storeConfig.StoreConfigId = this.storeConfig.StoreConfigId;

    this._storeConfigService.saveStoreConfig(storeConfig).subscribe({
      next: (response) => {
        this._snackbarService.openSnackBar(message, 'success');
        this._dialogRef.close(response);
      }
    });
  }

  get isPaymentBeneficiary(): boolean {
    return (
      this.form.controls['BeneficiaryType']?.value?.toLowerCase() ===
      'pagadoria'
    );
  }
}
